import React, { useState } from 'react';
import { Row, Col } from 'reactstrap';
import colors from 'config/colors';

const TONE_COLORS = { bad: '#c62828', warn: '#e65100' };
const MAX_STOPS_SHOWN = 6;

// A route's stops in order (nearest to the school first) as a connected path: a numbered dot per stop joined to the next by a
// line, with how far it is from the school - the same as the app's Transport report. Long routes show the first few stops and
// open up on a click.
const StopPath = ({ stops }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? stops : stops.slice(0, MAX_STOPS_SHOWN);
  return (
    <div>
      {shown.map((stop, index) => {
        const isLast = index === shown.length - 1 && (expanded || stops.length <= MAX_STOPS_SHOWN);
        return (
          <div key={`${stop.name}-${index}`} style={{ display: 'flex', minHeight: 36 }}>
            <div style={{ width: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: colors.accent,
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                {index + 1}
              </span>
              {!isLast && <span style={{ flex: 1, width: 2, background: `${colors.accent}55`, margin: '1px 0' }} />}
            </div>
            <div style={{ paddingLeft: 8, paddingBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 'bold', color: colors.accentDark }}>{stop.name}</div>
              {stop.distance != null && (
                <div style={{ fontSize: 11, color: colors.grey3 }}>{stop.distance} km from school</div>
              )}
            </div>
          </div>
        );
      })}
      {stops.length > MAX_STOPS_SHOWN && (
        <button
          type="button"
          onClick={() => setExpanded(value => !value)}
          style={{
            border: 0,
            background: 'none',
            padding: '4px 0',
            color: colors.accent,
            fontWeight: 'bold',
            fontSize: 12,
            cursor: 'pointer',
          }}>
          {expanded ? 'Show fewer stops' : `Show all ${stops.length} stops`}
        </button>
      )}
    </div>
  );
};

const Line = ({ label, children }) => (
  <div style={{ display: 'flex', padding: '3px 0' }}>
    <div style={{ width: 78, flexShrink: 0, color: colors.grey3, fontSize: 11, paddingTop: 1 }}>{label}</div>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

// The Routes report as one card per route, laid out like the app's: the route and how many students ride it (of the seats), what
// needs attention as small tags, the bus and driver (the phone dials), and the stops as a connected path.
// routes: the rows of buildRouteReport.
const RouteCards = ({ routes }) => (
  <Row>
    {routes.map(route => (
      <Col key={route.route} lg={6} xs={12} className="mb-3">
        <div
          style={{
            height: '100%',
            borderRadius: 12,
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            padding: '12px 14px',
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <b style={{ fontSize: 15, color: colors.grey1 }}>{route.route}</b>
            <span style={{ fontSize: 11, color: colors.grey3, whiteSpace: 'nowrap' }}>
              {route.students} {route.students === 1 ? 'student' : 'students'}
              {route.seatCount ? ` / ${route.seatCount} seats` : ''}
            </span>
          </div>
          {!!route.issueList.length && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {route.issueList.map(issue => (
                <span
                  key={issue.text}
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    borderRadius: 4,
                    padding: '1px 7px',
                    color: TONE_COLORS[issue.tone],
                    background: `${TONE_COLORS[issue.tone]}1A`,
                  }}>
                  {issue.text}
                </span>
              ))}
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            {route.assignmentList.length ? (
              route.assignmentList.map(item => (
                <Line key={`${item.bus}-${item.driver}`} label="Bus">
                  <div style={{ fontSize: 12 }}>
                    {item.bus} | {item.driver}
                  </div>
                  {!!item.phone && (
                    <a href={`tel:${item.phone}`} style={{ fontSize: 12, fontWeight: 'bold', color: colors.accent }}>
                      <i className="fas fa-phone-alt" style={{ fontSize: 10, marginRight: 6 }} />
                      {item.phone}
                    </a>
                  )}
                </Line>
              ))
            ) : (
              <Line label="Bus">
                <div style={{ fontSize: 12, color: TONE_COLORS.warn }}>Not assigned</div>
              </Line>
            )}
            <Line label={`Stops (${route.stopCount})`}>
              {route.stopList.length ? <StopPath stops={route.stopList} /> : <span style={{ fontSize: 12 }}>--</span>}
            </Line>
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

export default RouteCards;
